import { useAuth } from '@powerportalspro/react';
import { PortalUserType } from '@powerportalspro/core';
import type { ViewDefinition } from '@powerportalspro/react-fluent';

// The React twin of the Blazor demo's `AccountAndContactCustomViews.cs`: the same view ids, so
// both stacks pick up the same localized view names, and the same FetchXML, so both show the
// same rows.

export const ALL_ACCOUNTS_VIEW_ID = '91732AD4-B4FE-49FF-80CD-72B280EFF088';
export const MY_ACCOUNTS_VIEW_ID = '8B1DDAB6-D58F-4D28-81F3-5CDB1973CC50';
export const ALL_CONTACTS_VIEW_ID = '28299C6F-EBC0-4206-9E11-A373D4C9891F';
export const MY_CONTACTS_VIEW_ID = '25999A64-A871-4200-A38C-22ACD531F1A2';
export const EDITABLE_CONTACTS_VIEW_ID = '1e36ede8-5057-4334-b85c-c4c650232376';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** The signed-in user's id, or null for a visitor. */
export function useCurrentUserId(): string | null {
  const auth = useAuth();
  return auth.status === 'authenticated' ? (auth.user.userId ?? null) : null;
}

/** The signed-in user's contact id: null for a visitor, and for an internal user, who has none. */
export function useCurrentContactId(): string | null {
  const auth = useAuth();
  if (auth.status !== 'authenticated' || auth.user.portalUserType === PortalUserType.SystemUser) {
    return null;
  }
  return auth.user.userId ?? null;
}

// `withGender` shows each contact's gender too, as the grouped cards do.
// Every contact the user may read: the shared sample contacts, and a signed-in user's own. The view
// needs no filter for it — the table's row security narrows every query to those rows.
export function getAllContactsView(withGender = false): ViewDefinition {
  return {
    id: ALL_CONTACTS_VIEW_ID,
    tableName: 'contact',
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="emailaddress1" />
        <attribute name="fullname" />
        <attribute name="mobilephone" />
        <attribute name="parentcustomerid" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <attribute name="ppp_age" />
        <attribute name="birthdate" />${withGender ? '\n        <attribute name="gendercode" />' : ''}
        <order attribute="fullname" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'mobilephone', width: 200 },
      { columnName: 'emailaddress1', width: 225 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'ppp_age', width: 100 },
      { columnName: 'birthdate', width: 125 },
      ...(withGender ? [{ columnName: 'gendercode', width: 125 }] : []),
    ],
  };
}

export function getMyContactsView(currentUserId: string | null): ViewDefinition {
  const userId = currentUserId ?? EMPTY_GUID;
  return {
    id: MY_CONTACTS_VIEW_ID,
    tableName: 'contact',
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="emailaddress1" />
        <attribute name="fullname" />
        <attribute name="mobilephone" />
        <attribute name="parentcustomerid" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <attribute name="ppp_age" />
        <filter type="or">
          <condition attribute="ppp_owningportaluserid" operator="eq" value="${userId}" />
          <condition attribute="ownerid" operator="eq" value="${userId}" />
        </filter>
        <order attribute="fullname" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'mobilephone', width: 200 },
      { columnName: 'emailaddress1', width: 225 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'ppp_age', width: 100 },
    ],
  };
}

/**
 * The Inline Editing demo's view: the same rows as All Contacts, with a column of each editor type
 * in place of the read-only full name and age.
 */
export function getEditableContactsView(displayName: string): ViewDefinition {
  return {
    id: EDITABLE_CONTACTS_VIEW_ID,
    tableName: 'contact',
    displayName,
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="firstname" />
        <attribute name="lastname" />
        <attribute name="emailaddress1" />
        <attribute name="parentcustomerid" />
        <attribute name="gendercode" />
        <attribute name="birthdate" />
        <attribute name="creditlimit" />
        <attribute name="donotemail" />
        <order attribute="lastname" />
        <order attribute="firstname" />
      </entity>
    </fetch>`,
    columns: [
      // Last name first, as the rows are ordered.
      { columnName: 'lastname', width: 150 },
      { columnName: 'firstname', width: 150 },
      { columnName: 'emailaddress1', width: 230 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'gendercode', width: 130 },
      { columnName: 'birthdate', width: 150 },
      { columnName: 'creditlimit', width: 140 },
      { columnName: 'donotemail', width: 130 },
    ],
  };
}

// Every account the user may read, as the table's row security narrows every query to.
export function getAllAccountsView(): ViewDefinition {
  return {
    id: ALL_ACCOUNTS_VIEW_ID,
    tableName: 'account',
    fetchXml: `<fetch>
      <entity name="account">
        <attribute name="accountnumber" />
        <attribute name="address1_city" />
        <attribute name="name" />
        <attribute name="primarycontactid" />
        <attribute name="telephone1" />
        <attribute name="revenue" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <order attribute="name" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'name', width: 200 },
      { columnName: 'accountnumber', width: 200 },
      { columnName: 'telephone1', width: 200 },
      { columnName: 'address1_city', width: 200 },
      { columnName: 'revenue', width: 200 },
      { columnName: 'primarycontactid', width: 200 },
    ],
  };
}

export function getMyAccountsView(currentUserId: string | null): ViewDefinition {
  const userId = currentUserId ?? EMPTY_GUID;
  return {
    id: MY_ACCOUNTS_VIEW_ID,
    tableName: 'account',
    fetchXml: `<fetch>
      <entity name="account">
        <attribute name="accountnumber" />
        <attribute name="address1_city" />
        <attribute name="name" />
        <attribute name="primarycontactid" />
        <attribute name="telephone1" />
        <attribute name="revenue" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <order attribute="name" />
        <filter type="or">
          <condition attribute="ppp_owningportaluserid" operator="eq" value="${userId}" />
          <condition attribute="ownerid" operator="eq" value="${userId}" />
        </filter>
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'name', width: 200 },
      { columnName: 'accountnumber', width: 200 },
      { columnName: 'telephone1', width: 200 },
      { columnName: 'address1_city', width: 200 },
      { columnName: 'revenue', width: 200 },
      { columnName: 'primarycontactid', width: 200 },
    ],
  };
}
